import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Each question contributes up to its max weight. Options carry a 0–1 factor.
const QUESTIONS = [
    {
        id: 'data',
        weight: 22,
        q: 'How organized and accessible is your business data?',
        options: [
            { label: 'Scattered across paper, email, and spreadsheets', f: 0.1 },
            { label: 'Mostly in a few systems, some silos', f: 0.5 },
            { label: 'Centralized and well-structured (CRM/ERP, cloud)', f: 1 },
        ],
    },
    {
        id: 'process',
        weight: 20,
        q: 'How many repetitive, rules-based tasks does your team do daily?',
        options: [
            { label: 'Very few — work is mostly bespoke', f: 0.3 },
            { label: 'A fair amount of repeated manual work', f: 0.7 },
            { label: 'Lots — data entry, routing, summarizing, replies', f: 1 },
        ],
    },
    {
        id: 'tools',
        weight: 16,
        q: 'What does your current software stack look like?',
        options: [
            { label: 'Mostly offline / legacy on-prem tools', f: 0.2 },
            { label: 'Mix of cloud apps, limited integration', f: 0.6 },
            { label: 'Modern cloud apps with APIs/integrations', f: 1 },
        ],
    },
    {
        id: 'team',
        weight: 14,
        q: 'How comfortable is your team adopting new technology?',
        options: [
            { label: 'Resistant — change is hard here', f: 0.3 },
            { label: 'Open but needs guidance and training', f: 0.7 },
            { label: 'Eager — early adopters already using AI tools', f: 1 },
        ],
    },
    {
        id: 'goal',
        weight: 14,
        q: 'How clear are the outcomes you want from AI?',
        options: [
            { label: 'Curious but no specific goal yet', f: 0.3 },
            { label: 'A few pain points in mind', f: 0.7 },
            { label: 'Specific KPIs we want to move', f: 1 },
        ],
    },
    {
        id: 'governance',
        weight: 14,
        q: 'Do you have data privacy/security guardrails in place?',
        options: [
            { label: 'Not really — minimal IT policy', f: 0.2 },
            { label: 'Basic policies and access controls', f: 0.6 },
            { label: 'Strong governance, compliance-aware', f: 1 },
        ],
    },
];

const USE_CASES = {
    data: 'A unified data layer / reporting assistant so you can ask questions of your own business data.',
    process: 'Workflow automation — auto-routing, data entry, document summarization, and drafted replies.',
    tools: 'AI features layered onto the apps you already use via their APIs (no rip-and-replace).',
    team: 'A guided pilot with hands-on training so adoption sticks.',
    goal: 'A short discovery to define 2–3 high-ROI use-cases tied to measurable KPIs.',
    governance: 'A lightweight AI-use policy and secure deployment so data stays protected.',
};

const AiReadinessAssessmentPage = () => {
    usePageSeo({
        title: 'AI Readiness Assessment — Is Your Business Ready for AI?',
        description: 'Free 6-question AI readiness assessment for California businesses. Get your readiness tier and the highest-ROI AI use-cases to start with. By BUILDLAB.',
        keywords: 'AI readiness assessment, AI for business, business AI integration, AI automation, AI strategy, AI use cases, managed IT AI California, BUILDLAB',
    });

    const [answers, setAnswers] = useState({});
    const set = (id, idx) => setAnswers((a) => ({ ...a, [id]: idx }));

    const { score, answered, tier, tierColor, blurb, recos } = useMemo(() => {
        let earned = 0, max = 0, answeredCount = 0;
        const weak = [];
        QUESTIONS.forEach((qq) => {
            max += qq.weight;
            const idx = answers[qq.id];
            if (idx != null) {
                answeredCount += 1;
                const f = qq.options[idx].f;
                earned += qq.weight * f;
                if (f <= 0.6) weak.push(qq.id);
            }
        });
        const s = max > 0 ? Math.round((earned / max) * 100) : 0;
        let t = 'Exploring', col = '#FFC9A3', b = '';
        if (s >= 75) { t = 'AI-Ready'; col = '#7CFFB2'; b = 'You have the foundation to deploy AI now. The win is picking the right first use-cases and moving fast.'; }
        else if (s >= 50) { t = 'Emerging'; col = '#BFF59A'; b = 'You are close. A few foundational fixes will unlock meaningful, low-risk AI wins.'; }
        else if (s >= 25) { t = 'Foundational'; col = '#FFC9A3'; b = 'Start by tightening data and process basics, then layer AI onto a focused pilot.'; }
        else { t = 'Exploring'; col = '#FF8A8A'; b = 'Best first step is a discovery workshop to find where AI will actually pay off for you.'; }
        // recommended use-cases: weak areas first, fall back to strongest opportunities
        const recoIds = (weak.length ? weak : QUESTIONS.map((q) => q.id)).slice(0, 3);
        return { score: s, answered: answeredCount, tier: t, tierColor: col, blurb: b, recos: recoIds.map((id) => USE_CASES[id]) };
    }, [answers]);

    const allAnswered = answered === QUESTIONS.length;

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'AI Readiness Assessment'} pagesub={'Assessment'} pageTop={'Business IT'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Is your business ready for AI?</h2>
                                <p>
                                    Answer six quick questions to get your <strong>AI readiness tier</strong> and the highest-ROI
                                    use-cases to start with. Honest answers give the best roadmap — and it's all in your browser.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Questions */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '8px 28px 16px' }}>
                                    {QUESTIONS.map((qq, i) => (
                                        <div key={qq.id} style={{ padding: '16px 0', borderBottom: i < QUESTIONS.length - 1 ? '1px solid var(--bs-border-color)' : 'none' }}>
                                            <p className="mb-2" style={{ fontSize: '.95rem', fontWeight: 600 }}>{i + 1}. {qq.q}</p>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {qq.options.map((o, idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => set(qq.id, idx)}
                                                        className={`btn btn-sm ${answers[qq.id] === idx ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        style={{ textAlign: 'left' }}
                                                        aria-pressed={answers[qq.id] === idx}
                                                    >{o.label}</button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Result */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', position: 'sticky', top: 100 }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Your AI readiness</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 4px' }}>
                                        <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: tierColor }}>{score}</span>
                                        <span style={{ fontSize: '1.1rem', opacity: 0.7 }}>/ 100</span>
                                    </div>
                                    <div style={{ fontWeight: 700, color: tierColor, marginBottom: 8 }}>{tier}</div>
                                    {!allAnswered && (
                                        <p style={{ fontSize: '.82rem', opacity: 0.75 }}>{answered} of {QUESTIONS.length} answered — finish for your tier.</p>
                                    )}
                                    {allAnswered && <p style={{ fontSize: '.88rem', opacity: 0.92 }}>{blurb}</p>}
                                    {allAnswered && recos.length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                            <div style={{ fontSize: '.85rem', opacity: 0.85, marginBottom: 6 }}>Where to start:</div>
                                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: '.84rem', opacity: 0.92 }}>
                                                {recos.map((r, idx) => <li key={idx} style={{ marginBottom: 6 }}>{r}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    <Link to="/managed-it-offer" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Claim your free AI assessment">Claim your free AI assessment</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="AI Readiness Assessment"
                                        summary={`AI readiness ${score}/100 — tier: ${tier}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            This is a high-level readiness indicator, not a formal audit. New Managed IT subscribers receive a free,
                            in-depth AI Business Integration assessment from BUILDLAB that maps concrete use-cases, ROI, and a rollout plan.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default AiReadinessAssessmentPage;
